import HLS from 'hls-parser';
import { BaseError } from 'make-error';

export class InvalidHlsStream extends BaseError {
    constructor(message: string) {
        super(message);
    }
}

/**
 * Determines if the Hls stream is valid
 */
export async function isHlsStreamValid(streamUrl: string): Promise<boolean> {
    const [baseUrl, hlsPlaylist] = await getHlsPlaylist(streamUrl);
    if (hlsPlaylist.isMasterPlaylist && hlsPlaylist.variants) {
        return isMasterPlaylistValid(baseUrl, hlsPlaylist, 0);
    } else if (!hlsPlaylist.isMasterPlaylist) {
        return playListHasSegments(hlsPlaylist);
    }

    return false;
}

/**
 * Gets the hls-parser.js Playlist from the url
 * Returns a tuple containing the response url and the parsed HLS playlist
 * @see {@link https://github.com/kuu/hls-parser/#playlist-extends-data Playlist}
 */
async function getHlsPlaylist(
    url: string
): Promise<[string, HLS.types.MasterPlaylist | HLS.types.MediaPlaylist]> {
    const response = await fetch(url);
    const blob = await response.blob();

    const reader = new FileReader();
    reader.readAsText(blob);

    return new Promise((resolve, reject) => {
        reader.onload = function (event) {
            if (!event.target || typeof event.target.result !== 'string') {
                reject(new Error('Received an unexpected HLS manifest format'));
            } else {
                try {
                    resolve([
                        response.url.substring(0, response.url.lastIndexOf('/') + 1),
                        HLS.parse(event.target.result),
                    ]);
                } catch (err) {
                    reject(new Error('Unable to play HLS stream'));
                }
            }
        };
    });
}

/**
 * Determines if the hls-parser MasterPlaylist is valid
 * @see {@link https://github.com/kuu/hls-parser/#masterplaylist-extends-playlist MasterPlaylist}
 */
async function isMasterPlaylistValid(
    baseUrl: string,
    hlsParserMasterPlaylist: HLS.types.MasterPlaylist,
    index: number
): Promise<boolean> {
    if (index >= hlsParserMasterPlaylist.variants.length) {
        throw new Error('Unable to load HLS. Playlist is invalid.');
    }

    const variant: HLS.types.Variant | undefined = hlsParserMasterPlaylist.variants[index];

    if (variant) {
        const url = new URL(variant.uri, baseUrl);

        const [newBaseUrl, hlsPlaylist] = await getHlsPlaylist(url.toString());

        if (playListHasSegments(hlsPlaylist)) {
            return true;
        } else {
            baseUrl = newBaseUrl;
        }
    }

    return isMasterPlaylistValid(baseUrl, hlsParserMasterPlaylist, index + 1);
}

/**
 * Determines if the hls-parser MediaPlaylist has segments
 * @see {@link https://github.com/kuu/hls-parser/#mediaplaylist-extends-playlist MediaPlaylist}
 */
function playListHasSegments(
    playlist: HLS.types.MediaPlaylist | HLS.types.MasterPlaylist
): boolean {
    if ('segments' in playlist) {
        const firstSegment: HLS.types.Segment | undefined = playlist.segments[0];
        if (firstSegment && firstSegment.uri) {
            return true;
        }
    }

    return false;
}
